// RecipeSearchPage.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Alert,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Skeleton,
  IconButton,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ShoppingBasketOutlinedIcon from "@mui/icons-material/ShoppingBasketOutlined";

import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { FRONTEND_URL } from "../config/urls";
import { getPathHreflangLinks } from "../seo/hreflang";
import { buildRecipePath } from "../utils/recipeUrl";
import api from "../services/api";
import defaultRecipeImg from "../assets/default-recipe.png";
import debounce from "lodash.debounce";
import Pagination from "@mui/material/Pagination";

import { useLocationCtx } from "../context/LocationContext";
import { debugCountry, resolveCountryId, setSelectedCountry } from "../utils/country";
import LocationGate from "./LocationGate";
import { pathLangFrom } from "../utils/lang";
import FeatureGateDialog from "../components/modals/FeatureGateDialog";
import useFeatureGate from "../hooks/useFeatureGate";
import { resolveRecipeImage } from "../utils/assets";
import { userHasActiveEntitlement } from "../utils/entitlements";
import { trackEvent } from "../utils/analytics/gtag";

function formatMoney(amount, currency, locale) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat(locale || "en", {
      style: "currency",
      currency: currency || "EUR",
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency || ""}`.trim();
  }
}

function normalizeSearchText(v) {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/\u00A0/g, " ")   // NBSP -> normal space
    .replace(/\s+/g, " ")     // collapse multiple spaces
    .trim();                  // remove leading/trailing space
}

function readStoredPricingLocation() {
  if (typeof window === "undefined") return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem("incooking_location_v1") || "{}");
    return {
      country_id: parsed?.country ? String(parsed.country) : "",
      zip_code: parsed?.usZip ? String(parsed.usZip) : "",
      us_state: parsed?.usState ? String(parsed.usState) : "",
      store_location_id: parsed?.storeLocationId ? String(parsed.storeLocationId) : "",
    };
  } catch {
    return {};
  }
}

function appendPricingContextToPath(path, context = {}) {
  const params = new URLSearchParams();
  if (context.country_id) params.set("country_id", String(context.country_id));
  if (context.zip_code) params.set("zip_code", String(context.zip_code));
  if (context.us_state) params.set("us_state", String(context.us_state));
  if (context.store_location_id) params.set("store_location_id", String(context.store_location_id));
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}
/**
 * Premium-looking cost widget for recipe cards.
 * - estimated_recipe_cost: theoretical cost for the used quantity
 * - estimated_basket_cost: packaging cost (ceil packages)
 *
 * IMPORTANT: All copy is i18n-key based.
 */
function CardCostEstimate({ loading, estimate, currency, servings, locale, t }) {
  const missingCount = Number(estimate?.missing_count || 0);

  const recipeCost = estimate?.ingredient_cost ?? estimate?.estimated_recipe_cost;
  const basketCost = estimate?.package_cost ?? estimate?.estimated_basket_cost;
  const adjustedCost = estimate?.adjusted_cost ?? basketCost;
  const hasAnyCost = [recipeCost, basketCost, adjustedCost].some((value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0;
  });
  const pricingUnavailable =
    estimate?.pricing_available === false ||
    (!loading && !hasAnyCost && missingCount > 0 && String(currency || estimate?.currency || "").toUpperCase() === "USD");

  const title = t("cost_estimate.title", "Estimated cost");
  const subtitle = t("cost_estimate.for_servings", {
    defaultValue: "for {{count}} servings",
    count: servings || 4,
  });

  const note = t(
    "cost_estimate.note",
    "Estimate based on average prices/packaging — not the final price."
  );

  const recipeLabel = t("cost_estimate.recipe_cost", "Estimated recipe cost");
  const packagesLabel = t(
    "cost_estimate.packages_cost",
    "Estimated shopping cost"
  );
  const adjustedLabel = t("cost_estimate.adjusted_cost", "What you pay now");
  const missingText =
    missingCount > 0
      ? t("cost_estimate.missing_prices", {
        defaultValue: "Missing prices for {{count}} items",
        count: missingCount,
      })
      : null;

  return (
    <Box
      sx={{
        mt: 1.25,
        p: 1.25,
        borderRadius: 2.5,
        position: "relative",
        overflow: "hidden",
        border: (th) => `1px solid ${th.palette.divider}`,
        background:
          "linear-gradient(135deg, rgba(25,118,210,0.08), rgba(76,175,80,0.06))",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.45,
          background:
            "radial-gradient(circle at 20% 20%, rgba(25,118,210,0.18), transparent 55%), radial-gradient(circle at 80% 0%, rgba(76,175,80,0.14), transparent 55%)",
        }}
      />

      <Box sx={{ position: "relative" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <PaidOutlinedIcon fontSize="small" />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, lineHeight: 1.1 }}
            >
              {title}
            </Typography>
          </Stack>

          <Tooltip title={note}>
            <IconButton size="small" sx={{ opacity: 0.9 }}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Typography
          variant="caption"
          sx={{ display: "block", color: "text.secondary", mt: 0.25 }}
        >
          {subtitle}
        </Typography>

        <Divider sx={{ my: 1 }} />

        {loading ? (
          <Stack spacing={0.75}>
            <Skeleton variant="rounded" height={18} />
            <Skeleton variant="rounded" height={18} />
            <Skeleton variant="rounded" height={14} />
          </Stack>
        ) : pricingUnavailable ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t(
              "cost_estimate.prices_unavailable_area",
              "Prices are not yet available for this recipe in your area."
            )}
          </Alert>
        ) : (
          <Stack spacing={0.75}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {recipeLabel}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 900 }}>
                {formatMoney(recipeCost, currency, locale)}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <ShoppingBasketOutlinedIcon fontSize="small" />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {packagesLabel}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: 900 }}>
                {formatMoney(basketCost, currency, locale)}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {adjustedLabel}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 900, color: "success.main" }}>
                {formatMoney(adjustedCost, currency, locale)}
              </Typography>
            </Stack>

            <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.25 }}>
              {note}
            </Typography>

            {missingText && (
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                label={missingText}
                sx={{ alignSelf: "flex-start", mt: 0.5, fontWeight: 700 }}
              />
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

export default function RecipeSearchPage() {
  const paramsUrl = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  let lang = paramsUrl.lang;
  if (!lang) lang = i18n.language || "en";

  const effectiveLang = pathLangFrom(i18n.language);
  const locale = useMemo(() => {
    if (effectiveLang === "el" || effectiveLang === "gr") return "el-GR";
    if (effectiveLang === "en") return "en-US";
    return "en-US";
  }, [effectiveLang]);

  const navigate = useNavigate();

  const [foodTypes, setFoodTypes] = useState([]);
  const [mealTypes, setMealTypes] = useState([]);
  const [dietTypes, setDietTypes] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [countries, setCountries] = useState([]);
  const [religiousPrefs, setReligiousPrefs] = useState([]);

  const [filters, setFilters] = useState({
    query: "",
    difficulty: "",
    prep_time: "",
    calories: "",
    sort: "recent",
    food_type: "",
    meal_type: "",
    diet_type: "",
    season: "",
    country: "",
    religious_preference: "",
  });

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [favLoadingId, setFavLoadingId] = useState(null);

  // User / Premium
  const [user, setUser] = useState(null);
  const [checkingUser, setCheckingUser] = useState(true);

  const {
    country: ctxCountry,
    usZip: ctxUsZip,
    usState: ctxUsState,
    storeLocationId: ctxStoreLocationId,
    setCountry: setCtxCountry,
  } = useLocationCtx();
  const [openGate, setOpenGate] = useState(false);
  const [pendingRecipe, setPendingRecipe] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(1);

  // cost estimate map: { [recipeId]: {estimated_recipe_cost, estimated_basket_cost, missing_count} }
  const [costMap, setCostMap] = useState({});
  const [costLoading, setCostLoading] = useState(false);
  const [costCurrency, setCostCurrency] = useState(null);
  const [costServings, setCostServings] = useState(4);

  const urlPricingContext = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return {
      country_id: params.get("country_id") || params.get("country") || "",
      zip_code: params.get("zip_code") || params.get("us_zip") || params.get("zip") || "",
      us_state: params.get("us_state") || "",
      store_location_id: params.get("store_location_id") || "",
    };
  }, [location.search]);
  const storedPricingContext = useMemo(() => readStoredPricingLocation(), []);

  const resolvedCountry = resolveCountryId({ locationCountry: ctxCountry });
  const effectiveCountry = filters.country || urlPricingContext.country_id || resolvedCountry.id || storedPricingContext.country_id || "";

  const usZip = urlPricingContext.zip_code || ctxUsZip || storedPricingContext.zip_code || "";
  const usState = urlPricingContext.us_state || ctxUsState || storedPricingContext.us_state || "";
  const storeLocationId = String(urlPricingContext.store_location_id || ctxStoreLocationId || storedPricingContext.store_location_id || "");

  const isUSA = effectiveCountry === "2" || effectiveCountry === 2;

  // Load current user
  useEffect(() => {
    Promise.allSettled([api.users.getCurrentUser(), api.users.entitlements()])
      .then(([userResult, entitlementsResult]) => {
        if (userResult.status !== "fulfilled") {
          setUser(null);
          return;
        }
        const res = userResult.value;
        const u = res.data?.data || res.data?.user || res.data;
        const entitlements = entitlementsResult.status === "fulfilled"
          ? entitlementsResult.value?.data?.entitlements || null
          : null;
        setUser(u ? { ...u, entitlements } : null);
      })
      .catch(() => setUser(null))
      .finally(() => setCheckingUser(false));
  }, []);

  const isLoggedIn = !!user?.id;
  const isPremium = !!user?.id && userHasActiveEntitlement(user);

  const { gateOpen, gateConfig, gateActions } = useFeatureGate({
    isLoggedIn,
    isPremium,
    lang,
    navigate,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  const debouncedFetchRecipes = useMemo(
    () =>
      debounce(async (payload) => {
        setLoading(true);
        try {
          const res = await api.recipes.search(payload);
          setResults(res?.data?.data || []);
          setTotalPages(res?.data?.pages || 1);
        } catch (e) {
          console.error("Search error:", e);
          setResults([]);
          setTotalPages(1);
        }
        setLoading(false);
      }, 400),
    []
  );

  useEffect(() => {
    return () => debouncedFetchRecipes.cancel?.();
  }, [debouncedFetchRecipes]);

  useEffect(() => {
    if (isUSA) {
      localStorage.setItem("us_zip", usZip || "");
      localStorage.setItem("us_state", usState || "");
      localStorage.setItem("us_store_location_id", storeLocationId || "");
    }
  }, [isUSA, usZip, usState, storeLocationId]);

  const buildEffectiveFilters = useCallback(
    (base) => ({
      ...base,
      query: normalizeSearchText(base?.query || ""),   // ✅ εδώ
      lang: effectiveLang,
      country: effectiveCountry || "",
      zip_code: isUSA ? usZip || undefined : undefined,
      us_state: isUSA ? usState || undefined : undefined,
      store_location_id: isUSA ? storeLocationId || undefined : undefined,
      page,
      page_size: pageSize,
    }),
    [effectiveLang, effectiveCountry, isUSA, usZip, usState, storeLocationId, page, pageSize]
  );

  useEffect(() => {
    debouncedFetchRecipes(buildEffectiveFilters(filters));
  }, [
    filters,
    page,
    pageSize,
    effectiveCountry,
    isUSA,
    usZip,
    usState,
    storeLocationId,
    effectiveLang,
    debouncedFetchRecipes,
    buildEffectiveFilters,
  ]);

  useEffect(() => {
    debugCountry("search.resolve", {
      effectiveCountry,
      resolved: resolvedCountry,
      filtersCountry: filters.country || "",
    });
  }, [effectiveCountry, resolvedCountry, filters.country]);

  useEffect(() => {
    setPage(1);
  }, [effectiveCountry, usZip, usState, storeLocationId]);

  useEffect(() => {
    const params = { params: { lang: effectiveLang } };
    api.household.getFoodTypes(params).then((res) => setFoodTypes(res.data)).catch(() => { });
    api.household.getMealTypes(params).then((res) => setMealTypes(res.data)).catch(() => { });
    api.household.getDietTypes(params).then((res) => setDietTypes(res.data)).catch(() => { });
    api.household.getSeasons(params).then((res) => setSeasons(res.data)).catch(() => { });
    api.household.getCountryRecipes(params).then((res) => setCountries(res.data)).catch(() => { });
    api.household.getReligiousPreferences(params).then((res) => setReligiousPrefs(res.data)).catch(() => { });
  }, [effectiveLang]);

  const handleChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
    if (field === "country") {
      setSelectedCountry(value, "user");
      setCtxCountry(value, "user");
      debugCountry("search.setCountry", { value });
    }
  };

  const handleFavorite = async (recipeId, isFav) => {
    if (
      !gateActions.guardAuth({
        title: t("favorites.require_account.title", "Save to Favorites"),
        message: t(
          "favorites.require_account.body",
          "To save favorites, please log in or create an account."
        ),
      })
    )
      return;

    setFavLoadingId(recipeId);
    try {
      if (isFav) await api.favorites.remove(recipeId);
      else await api.favorites.add({ recipe_id: recipeId });

      setResults((prev) =>
        prev.map((r) => (r.id === recipeId ? { ...r, is_favorite: !isFav } : r))
      );
    } catch (e) { }
    setFavLoadingId(null);
  };

  const fetchRecipes = async () => {
    setLoading(true);
    debouncedFetchRecipes(buildEffectiveFilters(filters));
    try {
      const res = await api.recipes.search(buildEffectiveFilters(filters));
      const items = res?.data?.data || [];
      setResults(items);
      setTotalPages(res?.data?.pages || 1);
      trackEvent("recipe_search", {
        query_length: normalizeSearchText(filters.query || "").length,
        results_count: Array.isArray(items) ? items.length : 0,
        language: effectiveLang,
        is_guest: !isLoggedIn,
        difficulty: filters.difficulty || "",
        sort: filters.sort || "",
        country: effectiveCountry || "",
        is_usa: isUSA ? 1 : 0,
        page,
      });
    } catch {
      setResults([]);
      setTotalPages(1);
      trackEvent("recipe_search", {
        query_length: String(filters.query || "").length,
        results_count: 0,
        language: effectiveLang,
        is_guest: !isLoggedIn,
        difficulty: filters.difficulty || "",
        sort: filters.sort || "",
        country: effectiveCountry || "",
        is_usa: isUSA ? 1 : 0,
        page,
      });
    }
    setLoading(false);
  };

  // Fetch card costs
  useEffect(() => {
    const fetchCardCosts = async () => {
      if (!results?.length) {
        setCostMap({});
        return;
      }
      const ids = results.map((r) => r.id).filter(Boolean);
      if (!ids.length) return;

      setCostLoading(true);
      try {
        const payload = {
          recipe_ids: ids,
          servings: 4,
          lang: effectiveLang,
          country_id: effectiveCountry || "",
          zip_code: isUSA ? (usZip || "") : "",
          us_state: isUSA ? (usState || "") : "",
          store_location_id: isUSA ? (storeLocationId || "") : "",
        };

        const res = await api.recipes.cardsCostEstimate(payload);
        setCostMap(res?.data?.estimates || {});
        setCostCurrency(res?.data?.currency || (isUSA ? "USD" : "EUR"));
        setCostServings(res?.data?.servings || 4);
      } catch (e) {
        setCostMap({});
        setCostCurrency(isUSA ? "USD" : "EUR");
        setCostServings(4);
      } finally {
        setCostLoading(false);
      }
    };

    fetchCardCosts();
  }, [results, effectiveCountry, isUSA, usZip, usState, storeLocationId, effectiveLang]);

  const withAll = (arr) => {
    const list = Array.isArray(arr)
      ? arr
      : Array.isArray(arr?.data)
        ? arr.data
        : Array.isArray(arr?.items)
          ? arr.items
          : [];

    return [
      { id: "", name: t("common.all", "All") },
      ...list.map((o) => ({
        id: o.id ?? "",
        name:
          o.name ||
          o.type_name ||
          o.diet_name ||
          o.country_name ||
          o.preference_name ||
          o.label ||
          String(o.id ?? ""),
      })),
    ];
  };



  // const inFeedStep = isMobile ? 3 : 6;
  // const renderInFeedAd = (index) => {
  //   if (!showAds) return null;
  //   if ((index + 1) % inFeedStep === 0) {
  //     return (
  //       <Grid item xs={12} key={`ad-${index}`}>
  //         <AdSenseAd slot="2080997069" isPremium={isPremium} />
  //       </Grid>
  //     );
  //   }
  //   return null;
  // };

  const handleOpenRecipe = (rec) => {
    if (!effectiveCountry) {
      setPendingRecipe(rec);
      setOpenGate(true);
      return;
    }
    trackEvent("open_recipe_from_search", {
      recipe_id: rec?.id,
      recipe_slug: rec?.slug || undefined,
      lang,
    });

    navigate(appendPricingContextToPath(buildRecipePath(rec, lang), {
      country_id: effectiveCountry,
      zip_code: isUSA ? usZip : "",
      us_state: isUSA ? usState : "",
      store_location_id: isUSA ? storeLocationId : "",
    }));
  };

  const pathWithoutLocale =
    (location.pathname || "/").replace(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i, "") || "/";

  return (
    <Box sx={{ maxWidth: 1120, mx: "auto", mt: 4, mb: 6, px: { xs: 1, sm: 0 } }}>
      <Helmet>
        <link rel="canonical" href={`${FRONTEND_URL}${location.pathname || "/"}`} />
        {getPathHreflangLinks(pathWithoutLocale, "en")}
      </Helmet>
      {openGate && (
        <LocationGate
          forceOpen
          onClose={() => setOpenGate(false)}
          onLocationSaved={() => {
            setOpenGate(false);
            if (pendingRecipe) {
              navigate(appendPricingContextToPath(buildRecipePath(pendingRecipe, lang), {
                country_id: effectiveCountry,
                zip_code: isUSA ? usZip : "",
                us_state: isUSA ? usState : "",
                store_location_id: isUSA ? storeLocationId : "",
              }));
              setPendingRecipe(null);
            }
          }}
        />
      )}

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          mb: 1.5,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 900,
              letterSpacing: -0.3,
            }}
          >
            <SearchIcon color="primary" sx={{ fontSize: 36 }} />
            {t("recipes.search.title", "Search recipes")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
            {t(
              "recipes.search.subtitle",
              "Find recipes and see estimated cost per recipe card (for 4 servings)."
            )}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          {isPremium && (
            <Chip
              label={t("premium.badge_active", "Premium active — no ads")}
              color="success"
              variant="outlined"
              size="small"
              sx={{ fontWeight: 800 }}
            />
          )}
          {!!effectiveCountry && (
            <Chip
              label={t("recipes.search.country_badge", {
                defaultValue: "Country: {{country}}",
                country: String(effectiveCountry),
              })}
              variant="outlined"
              size="small"
              sx={{ fontWeight: 700 }}
            />
          )}
        </Stack>
      </Box>

      {/* User messaging */}
      {!checkingUser && !isLoggedIn && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {t("recipes.search.guest_info_title", "Discover recipes tailored to your country")}
          </Typography>
          <Typography variant="body2">
            {t(
              "recipes.search.guest_info_body",
              "As a guest you can search recipes and use filters. With a free account you can save favorites, and with Premium you remove ads."
            )}
          </Typography>
        </Alert>
      )}

      {!checkingUser && isLoggedIn && !isPremium && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            borderRadius: 2,
            border: (th) => `1px solid ${th.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {t("recipes.search.free_user_title", "You are using the recipe search as a free user")}
          </Typography>
          <Typography variant="body2">
            {t(
              "recipes.search.free_user_body",
              "You can save favorites and use filters. Premium removes ads."
            )}
          </Typography>
        </Alert>
      )}

      {/* Filters */}
      <Paper
        sx={{
          p: { xs: 2.25, sm: 3.5 },
          mb: 4,
          borderRadius: 4,
          boxShadow: 3,
          border: (th) => `1px solid ${th.palette.divider}`,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0))",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label={t("recipes.search.by_name", "Search by name")}
              fullWidth
              value={filters.query}
              onChange={(e) => handleChange("query", normalizeSearchText(e.target.value))}
              autoComplete="off"
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>{t("recipes.search.difficulty", "Difficulty")}</InputLabel>
              <Select
                value={filters.difficulty}
                label={t("recipes.search.difficulty", "Difficulty")}
                onChange={(e) => setFilters((f) => ({ ...f, difficulty: e.target.value }))}
              >
                <MenuItem value="">{t("common.all", "All")}</MenuItem>
                <MenuItem value="easy">{t("difficulty.easy", "Easy")}</MenuItem>
                <MenuItem value="medium">{t("difficulty.medium", "Medium")}</MenuItem>
                <MenuItem value="hard">{t("difficulty.hard", "Hard")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>{t("recipes.search.prep_time", "Prep time")}</InputLabel>
              <Select
                value={filters.prep_time}
                label={t("recipes.search.prep_time", "Prep time")}
                onChange={(e) => setFilters((f) => ({ ...f, prep_time: e.target.value }))}
              >
                <MenuItem value="">{t("common.all", "All")}</MenuItem>
                <MenuItem value="30">{t("prep_time.lt_30", "< 30'")}</MenuItem>
                <MenuItem value="60">{t("prep_time.between_30_60", "30-60'")}</MenuItem>
                <MenuItem value="90">{t("prep_time.gt_60", "> 60'")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>{t("recipes.search.calories", "Calories")}</InputLabel>
              <Select
                value={filters.calories}
                label={t("recipes.search.calories", "Calories")}
                onChange={(e) => setFilters((f) => ({ ...f, calories: e.target.value }))}
              >
                <MenuItem value="">{t("common.all", "All")}</MenuItem>
                <MenuItem value="300">{t("calories.up_to_300", "Up to 300")}</MenuItem>
                <MenuItem value="600">{t("calories.up_to_600", "Up to 600")}</MenuItem>
                <MenuItem value="601">{t("calories.over_600", "Over 600")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>{t("recipes.search.sort_by", "Sort by")}</InputLabel>
              <Select
                value={filters.sort}
                label={t("recipes.search.sort_by", "Sort by")}
                onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))}
              >
                <MenuItem value="recent">{t("sort.recent", "Recent")}</MenuItem>
                <MenuItem value="popular">{t("sort.popular", "Popular")}</MenuItem>
                <MenuItem value="fewest_ingredients">
                  {t("sort.fewest_ingredients", "Fewest ingredients")}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Accordion
          expanded={advancedOpen}
          onChange={() => setAdvancedOpen(!advancedOpen)}
          sx={{ mt: 2, bgcolor: (th) => th.palette.background.paper }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography color="primary" sx={{ fontWeight: 800 }}>
              {t("recipes.search.advanced_filters", "Advanced filters")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>{t("recipes.search.food_type", "Food type")}</InputLabel>
                  <Select
                    value={filters.food_type}
                    label={t("recipes.search.food_type", "Food type")}
                    onChange={(e) => setFilters((f) => ({ ...f, food_type: e.target.value }))}
                  >
                    {withAll(foodTypes).map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>{t("recipes.search.meal_type", "Meal type")}</InputLabel>
                  <Select
                    value={filters.meal_type}
                    label={t("recipes.search.meal_type", "Meal type")}
                    onChange={(e) => setFilters((f) => ({ ...f, meal_type: e.target.value }))}
                  >
                    {withAll(mealTypes).map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>{t("recipes.search.diet_type", "Diet type")}</InputLabel>
                  <Select
                    value={filters.diet_type}
                    label={t("recipes.search.diet_type", "Diet type")}
                    onChange={(e) => setFilters((f) => ({ ...f, diet_type: e.target.value }))}
                  >
                    {withAll(dietTypes).map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>{t("recipes.search.season", "Season")}</InputLabel>
                  <Select
                    value={filters.season}
                    label={t("recipes.search.season", "Season")}
                    onChange={(e) => setFilters((f) => ({ ...f, season: e.target.value }))}
                  >
                    {withAll(seasons).map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>{t("recipes.search.country", "Country")}</InputLabel>
                  <Select
                    value={filters.country}
                    label={t("recipes.search.country", "Country")}
                    onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
                  >
                    {withAll(countries).map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>
                    {t("recipes.search.religious_preference", "Religious preference")}
                  </InputLabel>
                  <Select
                    value={filters.religious_preference}
                    label={t("recipes.search.religious_preference", "Religious preference")}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, religious_preference: e.target.value }))
                    }
                  >
                    {withAll(religiousPrefs).map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Box sx={{ textAlign: "right", mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={fetchRecipes}
            size="large"
            startIcon={<SearchIcon />}
            sx={{ borderRadius: 2.5, px: 3, fontWeight: 900 }}
          >
            {t("common.search", "Search")}
          </Button>
        </Box>
      </Paper>

      {/* {showAds && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <AdSenseAd slot="2080997069" isPremium={isPremium} />
        </Box>
      )} */}

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {results.length === 0 && (
            <Grid item xs={12}>
              <Box
                sx={{
                  width: "100%",
                  textAlign: "center",
                  color: "text.secondary",
                  mt: 6,
                }}
              >
                <RestaurantMenuIcon color="disabled" sx={{ fontSize: 80, mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 900 }}>
                  {t("recipes.search.no_results", "No recipes found with these filters.")}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {t("recipes.search.try_less_filters", "Try removing some filters.")}
                </Typography>
                {/* {showAds && <AdSenseAd slot="2080997069" isPremium={isPremium} />} */}
              </Box>
            </Grid>
          )}

          {results.map((rec, idx) => {
            const estimate = costMap?.[String(rec.id)];
            return (
              <React.Fragment key={rec.id}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      borderRadius: 4,
                      boxShadow: 2,
                      overflow: "hidden",
                      border: (th) => `1px solid ${th.palette.divider}`,
                      transition: "transform 160ms ease, box-shadow 160ms ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    <Box sx={{ position: "absolute", top: 10, right: 12, zIndex: 10 }}>
                      <IconButton
                        color={rec.is_favorite ? "error" : "default"}
                        onClick={() => handleFavorite(rec.id, rec.is_favorite)}
                        aria-label={t("favorites.aria_label", "Add to favorites")}
                        disabled={favLoadingId === rec.id}
                        sx={{
                          bgcolor: "rgba(255,255,255,0.78)",
                          backdropFilter: "blur(6px)",
                          border: (th) => `1px solid ${th.palette.divider}`,
                          "&:hover": { bgcolor: "rgba(255,255,255,0.92)" },
                        }}
                      >
                        {rec.is_favorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                      </IconButton>
                    </Box>

                    <CardMedia
                      component="img"
                      height="190"
                      image={resolveRecipeImage(rec?.image_url)}
                      alt={rec.name}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = defaultRecipeImg;
                      }}
                      sx={{
                        objectFit: "cover",
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                      }}
                    />

                    <CardContent sx={{ pb: 1.75 }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontWeight: 900,
                          letterSpacing: -0.2,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          minHeight: 56,
                        }}
                      >
                        {rec.name}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t("recipes.search.difficulty", "Difficulty")}:{" "}
                        <b>{t(`difficulty.${rec.difficulty || "unknown"}`, rec.difficulty || "—")}</b>
                        {" • "}
                        {t("recipes.search.prep_time", "Prep time")}: <b>{rec.prep_time}'</b>
                        {" • "}
                        {t("recipes.search.calories", "Calories")}: <b>{rec.calories}</b>
                      </Typography>

                      <CardCostEstimate
                        loading={costLoading}
                        estimate={estimate}
                        currency={costCurrency || (isUSA ? "USD" : "EUR")}
                        servings={costServings || 4}
                        locale={locale}
                        t={t}
                      />
                    </CardContent>

                    <Box sx={{ flexGrow: 1 }} />

                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t("cost_estimate.badge", "Estimate")}
                        sx={{ fontWeight: 800, opacity: 0.9 }}
                      />

                      <Button
                        variant="outlined"
                        onClick={() => handleOpenRecipe(rec)}
                        sx={{ borderRadius: 2.5, fontWeight: 900, px: 2.25 }}
                      >
                        {t("common.details", "Details")}
                      </Button>
                    </Box>
                  </Card>
                </Grid>

                {/* {renderInFeedAd(idx)} */}
              </React.Fragment>
            );
          })}
        </Grid>
      )}

      {/* {showAds && results.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <AdSenseAd slot="2080997069"  isPremium={isPremium} />
        </Box>
      )} */}

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, val) => setPage(val)}
            color="primary"
            size="large"
          />
        </Box>
      )}

      <FeatureGateDialog
        open={gateOpen}
        onClose={gateActions.close}
        lang={lang}
        isLoggedIn={isLoggedIn}
        require={gateConfig.require}
        title={gateConfig.title}
        message={gateConfig.message}
        onLogin={gateActions.onLogin}
        onRegister={gateActions.onRegister}
        onUpgrade={gateActions.onUpgrade}
      />
    </Box>
  );
}
